import {
  customStyles,
  PrimaryButton,
  SecondaryButton,
  DropdownIndicator,
} from '../../../components';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import RangeSlider from 'routes/FraudDetection/Flows/EditProcess/RangeSlider';
import SettingsLayout from 'routes/Settings/SettingsLayout';
import cn from 'classnames';
import Select from 'react-select';
import {
  dayOptions,
  timeOptions,
  defaultTime,
  amPmOptions,
  defaultAmPm,
  durationUnitOptions,
  defaultUnit,
} from './configUtils';
import {dropdown} from 'assets/images/images';
import {useGatedQuery} from '../../FraudDetection/Flows/hooks/useGatedQuery';
import {useGatedMutation} from '../../FraudDetection/Flows/hooks/useGatedMutation';
import {
  fetchCompany,
  updateCompanyConfigurations,
} from '../../../requests/queries/flows';
import {useQueryClient} from 'react-query';
import {toast} from 'react-toastify';
import {
  convert24To12Hour,
  convert12To24Hour,
  getDayOption,
  convertSecondsToForm,
  convertFormToSeconds,
  formatDurationBreakdownFromSeconds,
} from '../helpers';
import {
  TierRangeSkeleton,
  SetupConditionsSkeleton,
  ActionButtonsSkeleton,
} from './ConfigurationSkeleton';
import {useDispatch, useSelector} from 'react-redux';
import {UPDATE_COMPANY_INFO} from '../../../store/types';

const LegendItem = ({colorClass, text}) => (
  <div className="flex items-center gap-2">
    <div className={cn('h-2.5 w-2.5 rounded-full', colorClass)} />
    <span className="text-body text-xs">{text}</span>
  </div>
);

const renderTierThumb = (props, state) => {
  let tooltipBackground;
  let afterBorderColor;

  if (state.index === 0) {
    tooltipBackground = 'bg-[#4A4A68]';
    afterBorderColor = 'after:border-b-[#4A4A68]';
  } else if (state.index === 1) {
    tooltipBackground = 'bg-[#DC6803]';
    afterBorderColor = 'after:border-b-[#DC6803]';
  } else {
    tooltipBackground = 'bg-status-404';
    afterBorderColor = 'after:border-b-status-404';
  }

  return (
    <div {...props} className="relative top-[2px]">
      <div className="aspect-square h-[12px] w-[12px] rounded-full bg-white border border-gray-300 shadow cursor-grab"></div>

      <div
        className={cn(
          "absolute bottom-[118%] left-1/2 -translate-x-1/2 px-[5px] mb-[3px] py-[2px] w-fit text-white text-[8px] rounded after:content-[''] after:absolute after:block after:w-0 after:h-0 after:border-l-transparent after:border-l-4 after:border-b-4 after:border-r-transparent after:border-r-4 after:rotate-180 after:left-1/2 after:-translate-x-1/2 after:-bottom-[3px]",
          tooltipBackground,
          afterBorderColor,
        )}
      >
        {state.valueNow}
      </div>
    </div>
  );
};

const renderTierTrack = (props, state) => {
  let trackColor;

  if (state.index === 0) {
    trackColor = 'bg-[#4A878D]';
  } else if (state.index === 1) {
    trackColor = 'bg-[#4A4A68]';
  } else if (state.index === 2) {
    trackColor = 'bg-[#DC6803]';
  } else {
    trackColor = 'bg-status-404';
  }

  return (
    <div
      {...props}
      key={`${props?.id}-${trackColor}`}
      className={cn(
        'h-1/4 top-1/2 -translate-y-1/2',
        {
          'rounded-l-full': state.index === 0,
          'rounded-r-full': state.index === state.value.length,
        },
        trackColor,
      )}
    ></div>
  );
};

function Configuration() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const {userDetails} = useSelector(state => state.auth);
  const [rangeValue, setRangeValue] = useState([35, 50, 69]);
  const [isSetupOpen, setIsSetupOpen] = useState(true);
  const [entityDetails, setEntityDetails] = useState({
    reporting_entity_name: userDetails?.company?.name || '',
    branch_name_or_id: '',
    rendering_entity_code: '',
  });

  // Weekend state
  const [weekendStart, setWeekendStart] = useState(dayOptions[0]);
  const [weekendEnd, setWeekendEnd] = useState(dayOptions[0]);

  // Nighttime state
  const [nightTimeStart, setNightTimeStart] = useState({
    time: defaultTime,
    amPm: defaultAmPm,
  });
  const [nightTimeEnd, setNightTimeEnd] = useState({
    time: defaultTime,
    amPm: defaultAmPm,
  });

  // Threshold states
  const [newUserThreshold, setNewUserThreshold] = useState({
    value: 0,
    unit: defaultUnit,
  });
  const [newDeviceThreshold, setNewDeviceThreshold] = useState({
    value: 0,
    unit: defaultUnit,
  });

  // Store original seconds values for accurate breakdown display
  const [newUserThresholdSeconds, setNewUserThresholdSeconds] = useState(0);
  const [newDeviceThresholdSeconds, setNewDeviceThresholdSeconds] = useState(0);

  // Store initial values for reset
  const [initialValues, setInitialValues] = useState(null);

  const {data, isLoading} = useGatedQuery(
    ['pr-company-details'],
    () => fetchCompany(),
    {
      onSuccess: data => {
        if (data?.entity && isProfiledRisk) {
          const {name, ...rest} = data.entity;
          dispatch({payload: rest, type: UPDATE_COMPANY_INFO});
        }
      },
    },
  );

  const companyData = useMemo(
    () => (!isLoading && data?.entity?.setup_config) || {},
    [data?.entity, isLoading],
  );

  // Initialize all state from companyData
  useEffect(() => {
    if (companyData && Object.keys(companyData).length > 0) {
      // Initialize tier ranges
      if (companyData?.tier_ranges) {
        const parsedRanges = [
          companyData?.tier_ranges?.low?.max || 35,
          companyData?.tier_ranges?.medium?.max || 50,
          companyData?.tier_ranges?.high?.max || 69,
        ];
        setRangeValue(parsedRanges);
      }

      // Initialize weekend
      if (companyData?.is_weekend) {
        setWeekendStart(getDayOption(companyData.is_weekend.start));
        setWeekendEnd(getDayOption(companyData.is_weekend.end));
      }

      // Initialize nighttime
      if (companyData?.is_night_time) {
        setNightTimeStart(convert24To12Hour(companyData.is_night_time.start));
        setNightTimeEnd(convert24To12Hour(companyData.is_night_time.end));
      }

      // Initialize thresholds
      if (companyData?.new_user_threshold !== undefined) {
        const thresholdForm = convertSecondsToForm(
          companyData.new_user_threshold,
        );
        setNewUserThreshold(thresholdForm);
        // Store original seconds for accurate breakdown
        setNewUserThresholdSeconds(companyData.new_user_threshold);
      }
      if (companyData?.new_device_threshold !== undefined) {
        const thresholdForm = convertSecondsToForm(
          companyData.new_device_threshold,
        );
        setNewDeviceThreshold(thresholdForm);
        // Store original seconds for accurate breakdown
        setNewDeviceThresholdSeconds(companyData.new_device_threshold);
      }

      if (companyData?.entity_details) {
        setEntityDetails({
          reporting_entity_name:
            companyData?.entity_details?.reporting_entity_name ||
            userDetails?.company?.name,
          branch_name_or_id:
            companyData?.entity_details?.branch_name_or_id || '',
          rendering_entity_code:
            companyData?.entity_details?.rendering_entity_code || '',
        });
      }

      // Store initial values for reset (including original seconds)
      setInitialValues({
        rangeValue: companyData?.tier_ranges
          ? [
              companyData?.tier_ranges?.low?.max || 35,
              companyData?.tier_ranges?.medium?.max || 50,
              companyData?.tier_ranges?.high?.max || 69,
            ]
          : [35, 50, 69],
        weekendStart: companyData?.is_weekend
          ? getDayOption(companyData.is_weekend.start)
          : dayOptions[0],
        weekendEnd: companyData?.is_weekend
          ? getDayOption(companyData.is_weekend.end)
          : dayOptions[0],
        nightTimeStart: companyData?.is_night_time
          ? convert24To12Hour(companyData.is_night_time.start)
          : {time: defaultTime, amPm: defaultAmPm},
        nightTimeEnd: companyData?.is_night_time
          ? convert24To12Hour(companyData.is_night_time.end)
          : {time: defaultTime, amPm: defaultAmPm},
        newUserThreshold:
          companyData?.new_user_threshold !== undefined
            ? convertSecondsToForm(companyData.new_user_threshold)
            : {value: 0, unit: defaultUnit},
        newDeviceThreshold:
          companyData?.new_device_threshold !== undefined
            ? convertSecondsToForm(companyData.new_device_threshold)
            : {value: 0, unit: defaultUnit},
        newUserThresholdSeconds: companyData?.new_user_threshold || 0,
        newDeviceThresholdSeconds: companyData?.new_device_threshold || 0,
        entityDetails: {
          reporting_entity_name:
            companyData?.entity_details?.reporting_entity_name ||
            userDetails?.company?.name,
          branch_name_or_id:
            companyData?.entity_details?.branch_name_or_id || '',
          rendering_entity_code:
            companyData?.entity_details?.rendering_entity_code || '',
        },
      });
    }
  }, [companyData, userDetails, isLoading]);

  // Mutation for updating configurations
  const {mutate: updateConfig, isLoading: isUpdating} = useGatedMutation(
    updateCompanyConfigurations,
    {
      onSuccess: () => {
        toast.success('Configuration updated successfully');
        queryClient.invalidateQueries(['pr-company-details']);
      },
      onError: error => {
        toast.error(
          error?.response?.data?.error || 'Failed to update configuration',
        );
      },
    },
  );

  // Prepare data for API
  const prepareApiData = () => {
    // Calculate tier ranges from slider values
    const tierRanges = {
      low: {
        min: 0,
        max: rangeValue[0],
        description: 'Low risk',
      },
      medium: {
        min: rangeValue[0] + 1,
        max: rangeValue[1],
        description: 'Medium risk',
      },
      high: {
        min: rangeValue[1] + 1,
        max: rangeValue[2],
        description: 'High risk',
      },
      very_high: {
        min: rangeValue[2] + 1,
        max: 100,
        description: 'Very high risk',
      },
    };

    return {
      tier_ranges: tierRanges,
      is_weekend: {
        start:
          weekendStart.value.charAt(0).toUpperCase() +
          weekendStart.value.slice(1),
        end:
          weekendEnd.value.charAt(0).toUpperCase() + weekendEnd.value.slice(1),
      },
      is_night_time: {
        start: convert12To24Hour(nightTimeStart.time, nightTimeStart.amPm),
        end: convert12To24Hour(nightTimeEnd.time, nightTimeEnd.amPm),
      },
      new_user_threshold: newUserThresholdSeconds,
      new_device_threshold: newDeviceThresholdSeconds,
      entity_details: {
        reporting_entity_name: entityDetails.reporting_entity_name,
        branch_name_or_id: entityDetails.branch_name_or_id,
        rendering_entity_code: entityDetails.rendering_entity_code,
      },
    };
  };

  const handleSave = () => {
    const apiData = prepareApiData();
    updateConfig({setup_config: apiData});
  };

  const handleReset = () => {
    if (initialValues) {
      setRangeValue(initialValues.rangeValue);
      setWeekendStart(initialValues.weekendStart);
      setWeekendEnd(initialValues.weekendEnd);
      setNightTimeStart(initialValues.nightTimeStart);
      setNightTimeEnd(initialValues.nightTimeEnd);
      setNewUserThreshold(initialValues.newUserThreshold);
      setNewDeviceThreshold(initialValues.newDeviceThreshold);
      // Reset seconds values from initial values (use stored seconds, not recalculated)
      setNewUserThresholdSeconds(initialValues.newUserThresholdSeconds || 0);
      setNewDeviceThresholdSeconds(
        initialValues.newDeviceThresholdSeconds || 0,
      );
      setEntityDetails(initialValues.entityDetails);
      toast.success('Configuration reset to saved values');
    }
  };

  const handleEntityDetailsChange = useCallback(e => {
    const {name: field, value} = e.target;
    setEntityDetails(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const isProfiledRisk = localStorage
    ?.getItem('subdomain')
    ?.includes('profiledrisk');

  if (isLoading) {
    return (
      <SettingsLayout breadCrumbs="Settings">
        <ActionButtonsSkeleton />
        <TierRangeSkeleton />
        <SetupConditionsSkeleton />
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout breadCrumbs="Settings" hideSub={isProfiledRisk}>
      <div className="flex items-center gap-2 justify-end my-6">
        <SecondaryButton
          buttonText="Reset"
          onClick={handleReset}
          loading={isLoading}
          disabled={isUpdating || !initialValues}
        />
        <PrimaryButton
          buttonText={'Save Changes'}
          onClick={handleSave}
          disabled={isUpdating}
          loading={isUpdating}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-8 mb-8">
        <div className="border border-[#F7F7F7] rounded-2xl">
          <div className="bg-[#F7F7F7] py-2.5 px-6 rounded-t-2xl text-tiny text-[#0B1C56] font-medium">
            Setup Entity Details
          </div>

          <div className="px-6 pt-1.5 pb-[10px] flex flex-col gap-4">
            <div>
              <label className="text-small text-body mb-2 block">
                Reporting Entity Name
              </label>
              <input
                type="text"
                className="!h-8 !text-small"
                placeholder="Enter entity name"
                value={entityDetails.reporting_entity_name}
                name="reporting_entity_name"
                onChange={handleEntityDetailsChange}
              />
            </div>

            <div>
              <label className="text-small text-body mb-2 block">
                Branch Name / ID / Code
              </label>
              <input
                type="text"
                className="!h-8 !text-small"
                placeholder="Enter branch details"
                value={entityDetails.branch_name_or_id}
                name="branch_name_or_id"
                onChange={handleEntityDetailsChange}
              />
            </div>

            <div>
              <label className="text-small text-body mb-2 block">
                Rendering Entity Code
              </label>
              <input
                type="text"
                className="!h-8 !text-small"
                placeholder="Enter rendering code"
                value={entityDetails.rendering_entity_code}
                name="rendering_entity_code"
                onChange={handleEntityDetailsChange}
              />
            </div>
          </div>
        </div>
        <div className="border border-[#F7F7F7] rounded-2xl">
          <div className="bg-[#F7F7F7] py-2.5 px-6 rounded-t-2xl text-tiny text-[#0B1C56] font-medium">
            Edit tier range
          </div>
          <div className="pt-11 px-6 pb-8">
            <div>
              <p className="mb-2 text-body text-sm text-start">Tier</p>
              <p className="text-body text-[11px] text-start">
                Move slider to set risk tier range for your profiles
              </p>
            </div>

            <div className="relative !mt-8">
              <div className="absolute bottom-[2px] left-[-6px] h-[12px] z-[9] w-[12px] flex items-center bg-[#D2D2D2] rounded-full aspect-square cursor-not-allowed" />
              <div className="cursor-not-allowed bg-[#4A878D] absolute bottom-[19px] -left-[11px] px-[8px] py-[3px] text-white text-[8px] rounded after:content-[''] after:absolute after:block after:w-0 after:h-0 after:border-l-transparent after:border-l-4 after:border-b-4 after:border-r-transparent after:border-r-4 after:rotate-180 after:left-1/2 after:-translate-x-1/2 after:-bottom-[3px] after:border-b-[#4A878D]">
                0
              </div>

              <RangeSlider
                min={0}
                max={100}
                onChange={setRangeValue}
                className="w-full h-4"
                value={rangeValue}
                renderThumb={renderTierThumb}
                renderTrack={renderTierTrack}
              />

              <div className="absolute bottom-[2px] right-[-6px] h-[12px] w-[12px] flex items-center bg-[#D2D2D2] rounded-full aspect-square cursor-not-allowed" />
              <div className="cursor-not-allowed bg-status-404 absolute bottom-[19px] right-[-9px] p-[2px] text-white text-[8px] rounded after:content-[''] after:absolute after:block after:w-0 after:h-0 after:border-l-transparent after:border-l-4 after:border-b-4 after:border-r-transparent after:border-r-4 after:rotate-180 after:left-1/2 after:-translate-x-1/2 after:-bottom-[3px] after:border-b-status-404">
                100
              </div>
            </div>

            <div className="flex items-center gap-4 mt-6">
              <LegendItem colorClass="bg-[#4A878D]" text="Low" />
              <LegendItem colorClass="bg-[#4A4A68]" text="Medium" />
              <LegendItem colorClass="bg-[#DC6803]" text="High" />
              <LegendItem colorClass="bg-status-404" text="Very High" />
            </div>
          </div>
        </div>
      </div>
      <div className="border border-[#F7F7F7] rounded-2xl mt-8">
        <div
          className={cn(
            'bg-[#F7F7F7] py-2.5 px-6 rounded-t-2xl text-tiny text-[#0B1C56] font-medium w-full flex justify-between items-center cursor-pointer',
            {
              'rounded-b-2xl': !isSetupOpen,
            },
          )}
          onClick={() => setIsSetupOpen(!isSetupOpen)}
        >
          <span>Setup Conditions</span>
          <img src={dropdown} alt="" width={16} height={16} />
        </div>

        {isSetupOpen && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-11 bg-white rounded-b-2xl">
            <div className="bg-[#F9F9F9] p-4 rounded-lg">
              <p className="text-body text-sm text-start mb-4 font-medium">
                Set the start and end days for the &quot;is weekend&quot;
                condition
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex-1">
                  <label className="text-small text-body mb-1 block">
                    Start day
                  </label>
                  <Select
                    options={dayOptions}
                    value={weekendStart}
                    onChange={setWeekendStart}
                    styles={customStyles}
                    menuPlacement="auto"
                    classNamePrefix="react-select"
                    components={{DropdownIndicator}}
                  />
                </div>
                <div className="flex-1">
                  <label className="text-small text-body mb-1 block">
                    End day
                  </label>
                  <Select
                    options={dayOptions}
                    value={weekendEnd}
                    onChange={setWeekendEnd}
                    styles={customStyles}
                    menuPlacement="auto"
                    classNamePrefix="react-select"
                    components={{DropdownIndicator}}
                  />
                </div>
              </div>
            </div>

            <div className="bg-[#F9F9F9] p-4 rounded-lg">
              <p className="text-body text-sm text-start mb-4 font-medium ">
                Set the start and end times for the &quot;is Night Time&quot;
                condition
              </p>
              <div className="grid lg:grid-cols-2 gap-4">
                <div className="flex-1">
                  <label className="text-small text-body mb-1 block">
                    Start time
                  </label>

                  <div className="grid grid-cols-[55%_40%] gap-2">
                    <Select
                      options={timeOptions}
                      value={nightTimeStart.time}
                      onChange={option =>
                        setNightTimeStart({...nightTimeStart, time: option})
                      }
                      styles={customStyles}
                      menuPlacement="auto"
                      classNamePrefix="react-select"
                      components={{DropdownIndicator}}
                    />
                    <Select
                      options={amPmOptions}
                      value={nightTimeStart.amPm}
                      onChange={option =>
                        setNightTimeStart({...nightTimeStart, amPm: option})
                      }
                      styles={customStyles}
                      menuPlacement="auto"
                      classNamePrefix="react-select"
                      components={{DropdownIndicator}}
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <label className="text-small text-body mb-1 block">
                    End time
                  </label>
                  <div className="grid grid-cols-[55%_40%] gap-2">
                    <Select
                      options={timeOptions}
                      value={nightTimeEnd.time}
                      onChange={option =>
                        setNightTimeEnd({...nightTimeEnd, time: option})
                      }
                      styles={customStyles}
                      menuPlacement="auto"
                      classNamePrefix="react-select"
                      components={{DropdownIndicator}}
                    />
                    <Select
                      options={amPmOptions}
                      value={nightTimeEnd.amPm}
                      onChange={option =>
                        setNightTimeEnd({...nightTimeEnd, amPm: option})
                      }
                      styles={customStyles}
                      menuPlacement="auto"
                      classNamePrefix="react-select"
                      components={{DropdownIndicator}}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#F9F9F9] p-4 rounded-lg">
              <p className="text-body text-sm text-start mb-4 font-medium">
                Set how long a user is considered &quot;new&quot; after signup
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <input
                    type="number"
                    value={newUserThreshold.value || ''}
                    onChange={e => {
                      const newValue = e.target.value;
                      setNewUserThreshold({
                        ...newUserThreshold,
                        value: newValue,
                      });
                      // Update seconds when user changes value
                      const seconds = convertFormToSeconds(
                        newValue,
                        newUserThreshold.unit,
                      );
                      setNewUserThresholdSeconds(seconds);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                    min="0"
                  />
                  {newUserThresholdSeconds > 0 && (
                    <p className="text-xs text-body mt-1.5 text-gray-600">
                      {formatDurationBreakdownFromSeconds(
                        newUserThresholdSeconds,
                      )}
                    </p>
                  )}
                </div>
                <Select
                  options={durationUnitOptions}
                  value={newUserThreshold.unit}
                  onChange={option => {
                    setNewUserThreshold({...newUserThreshold, unit: option});
                    // Update seconds when user changes unit
                    const seconds = convertFormToSeconds(
                      newUserThreshold.value,
                      option,
                    );
                    setNewUserThresholdSeconds(seconds);
                  }}
                  styles={customStyles}
                  menuPlacement="auto"
                  classNamePrefix="react-select"
                  components={{DropdownIndicator}}
                />
              </div>
            </div>

            <div className="bg-[#F9F9F9] p-4 rounded-lg">
              <p className="text-body text-sm text-start mb-4 font-medium ">
                Set how long a device is considered &quot;new&quot; after its
                first use
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <input
                    type="number"
                    value={newDeviceThreshold.value || ''}
                    onChange={e => {
                      const newValue = e.target.value;
                      setNewDeviceThreshold({
                        ...newDeviceThreshold,
                        value: newValue,
                      });
                      // Update seconds when user changes value
                      const seconds = convertFormToSeconds(
                        newValue,
                        newDeviceThreshold.unit,
                      );
                      setNewDeviceThresholdSeconds(seconds);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                    min="0"
                  />
                  {newDeviceThresholdSeconds > 0 && (
                    <p className="text-xs text-body mt-1.5 text-gray-600">
                      {formatDurationBreakdownFromSeconds(
                        newDeviceThresholdSeconds,
                      )}
                    </p>
                  )}
                </div>
                <Select
                  options={durationUnitOptions}
                  value={newDeviceThreshold.unit}
                  onChange={option => {
                    setNewDeviceThreshold({
                      ...newDeviceThreshold,
                      unit: option,
                    });
                    // Update seconds when user changes unit
                    const seconds = convertFormToSeconds(
                      newDeviceThreshold.value,
                      option,
                    );
                    setNewDeviceThresholdSeconds(seconds);
                  }}
                  styles={customStyles}
                  menuPlacement="auto"
                  classNamePrefix="react-select"
                  components={{DropdownIndicator}}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </SettingsLayout>
  );
}

export default Configuration;
